<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { ScoresExporterAlternativeReadModel } from "@Evaluation/AlternativesSurveys/Domain/readmodels";
    import { ScoresExporterViewModel } from "@Evaluation/AlternativesSurveys/Components/ScoresExporter";

@Component({
    props: {
        viewModel: {
            type: Object,
            required: true
        }
    }
})
export default class ScoresExporter extends Vue
{
    get vm(): ScoresExporterViewModel { return this.$props.viewModel as ScoresExporterViewModel; }
    get opened(): boolean { return this.vm?.opened ?? false; }

    get MappedAttributeIds(): number[] { return this.vm.criteria.filter(a => a.AttributeId).map(a => a.AttributeId); }

    IsExact(alternative: ScoresExporterAlternativeReadModel, attrId: number): boolean
    {
        return alternative.Scores.find(a => a.AttributeId === attrId).IsExact;
    }

    HasValue(alternative: ScoresExporterAlternativeReadModel, attrId: number): boolean
    {
        const score = alternative.Scores.find(a => a.AttributeId === attrId);
        return score.AttributeValue != null;
    }

    HasDefinedAttributeValues(alternative: ScoresExporterAlternativeReadModel, attrId: number): boolean
    {
        const score = alternative.Scores.find(a => a.AttributeId === attrId);
        return score.HasDefinedAttributeValues;
    }


    getScore(alternative: ScoresExporterAlternativeReadModel, attrId: number): number
    {
        return alternative.Scores.find(a => a.AttributeId === attrId).Score;
    }

    getAttributeValue(alternative: ScoresExporterAlternativeReadModel, attrId: number): number
    {
        return alternative.Scores.find(a => a.AttributeId === attrId).AttributeValue;
    }

    getAttributeValueLabel(alternative: ScoresExporterAlternativeReadModel, attrId: number): string
    {
        return alternative.Scores.find(a => a.AttributeId === attrId).AttributeValueLabel;
    }

    getAttributeName(attrId: number): string
    {
        return this.vm.attributes.find(a => a.Id === attrId).Name;
    }

    close()
    {
        this.$emit('close-dialog');
    }

    exportScores()
    {
        const mappings = this.vm.criteria.filter(c => c.AttributeId != null).map(c => ({
            RefCriterionId: c.Id,
            AttributeId: c.AttributeId
        }));

        this.$emit('export-scores', mappings);
    }

    resetPreview()
    {
        this.$emit('reset-preview');
    }

    loadPreview()
    {
        const mappings = this.vm.criteria.filter(c => c.AttributeId != null).map(c => ({
            RefCriterionId: c.Id,
            AttributeId: c.AttributeId
        }));

        this.$emit('load-preview', mappings);
    }

    createAttribute()
    {
        this.$emit('show-attribute-creator');
    }
}
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Save scores to attributes</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <template v-if="vm.initialized === false">
                            <h3><i class="fa fa-spinner fa-spin"></i> Loading...</h3>
                        </template>

                        <template v-else-if="vm.initialized === true && vm.preview == null">
                            <table class="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Criterion</th>
                                        <th>Attribute</th>
                                    </tr>
                                </thead>
                                <tbody >
                                    <tr v-for="c in vm.criteria" :key="c.Id">
                                        <td>
                                            {{c.Name}}
                                            <br />
                                            <span class="text-muted">{{c.Path}}</span>
                                        </td>
                                        <td>
                                            <select class="form-control" v-model="c.AttributeId">
                                                <option :value="null">None</option>
                                                <option v-for="attr in vm.attributes" :key="attr.Id" :value="attr.Id">{{attr.Name}}</option>
                                            </select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </template>
                        
                        <template v-else-if="vm.initialized === true && vm.preview != null">
                            <table class="table table-hover table-bordered">
                                <thead>
                                    <tr>
                                        <th>Alternative</th>
                                        <th v-for="attrId in MappedAttributeIds" :key="attrId">{{getAttributeName(attrId)}}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="alternative in vm.alternatives" :key="alternative.Name">
                                        <td>
                                            {{alternative.Name}}
                                        </td>

                                        <td v-for="attrId in MappedAttributeIds" :key="attrId" :class="{'bg-warning': IsExact(alternative, attrId) === false}">
                                            <div v-if="HasValue(alternative, attrId)">
                                                <span v-if="HasDefinedAttributeValues(alternative, attrId) === false">{{getScore(alternative, attrId)}}</span>
                                                <span v-if="HasDefinedAttributeValues(alternative, attrId) === true">
                                                    {{getAttributeValueLabel(alternative, attrId)}} ({{getAttributeValue(alternative, attrId)}})
                                                </span>

                                                <span v-if="IsExact(alternative, attrId) === false" class="text-muted">
                                                    <br />Source score: {{getScore(alternative, attrId)}}
                                                </span>
                                            </div>

                                        </td>

                                    </tr>
                                </tbody>
                            </table>

                        </template>

                    </div>

                    <div class="modal-footer">
                        <template v-if="vm.preview == null">
                            <button type="button" @click="createAttribute" class="btn btn-outline-secondary">New attribute</button>
                            <button type="button" @click="loadPreview" :disabled="MappedAttributeIds.length == 0" class="btn btn-primary">Preview</button>
                        </template>
                        <template v-else>
                            <button type="button" @click="resetPreview" class="btn btn-outline-secondary">Back</button>
                            <button type="button" @click="exportScores" class="btn btn-success">Save</button>
                        </template>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template>  